/**
 * WpPackageConfiguratorV3 — Zeilen-Tabelle wie Easybill
 * =====================================================
 * Ersetzt v2 als primärer WP-Konfigurator. Kein Base-Price/Delta-Zauber mehr —
 * jede Zeile hat einen echten Preis; Total = einfache Summe. Datenstruktur
 * matched 1:1 auf Easybill-Positionen (spätere Push-Integration).
 *
 * Flow:
 *   1) Paket-Auswahl → GET /wp-packages/{id}/expand-lines liefert:
 *      • 1 Bundle-Zeile (Netto = abgabepreis_brutto/1.19 − Σ separate items)
 *      • N separate Zeilen für items mit separate_line=True (Puffer etc.)
 *   2) Zeilen sind editierbar: Bezeichnung, Menge, Netto-Preis. Brutto rechnet
 *      sich automatisch (VAT 19 %).
 *   3) „+ Position": Katalog-Picker (Hydraulisch/Elektrisch/Hardware/…) oder
 *      Freie Zeile (leerer Text + Preis). Neue Zeile ans Ende.
 *   4) 🗑️ Löschen: Zeile weg → Summe fällt entsprechend.
 *   5) Gesamt-Rabatt (%) unten optional.
 *
 * Snapshot fürs Angebot: alle Zeilen mit ``is_premarged: true`` → Backend
 * rechnet keine Marge drauf.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Droplets, Zap, Package as PackageIcon,
  Wrench, ClipboardList, Layers, X, Pencil, Tag, Percent, Euro, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { sortOfferItems } from "@/lib/offerSort";
import { EuroInput } from "@/components/EuroInput";
import { DatasheetButton } from "@/components/datasheets/DatasheetButton";
import { useWpDatasheets } from "@/components/datasheets/useDatasheets";
import RepriceBanner from "./RepriceBanner";

const VAT = 0.19;
const fmt = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

/**
 * Standard-KfW-458-Annahme für die Paket-Vergleichsansicht auf den Cards
 * (Rechtsstand ab 09.07.2026): 30 % Grundförderung + 16 % Klimageschwindig-
 * keitsbonus = 46 % — Standard-Fall Heizungstausch, ohne Einkommensnachweis.
 * Bemessungsgrundlage: min(brutto, 28.000 €).
 */
const KFW_CAP_EUR = 28000.0;
const KFW_STD_PCT = 46.0;   // 30 (Grund) + 16 (Klima)
const kfwFor = (grossEur) => {
  const g = Number(grossEur) || 0;
  const eligible = Math.min(g, KFW_CAP_EUR);
  const subsidy = Math.round((eligible * KFW_STD_PCT) / 100.0);
  const own = Math.max(0, Math.round(g) - subsidy);
  return { subsidy, own };
};

const CAT_META = {
  bundle:      { label: "Paket",       icon: PackageIcon,   color: "text-emerald-300" },
  // Iter89i (2026-02-20) — Einheitliche 6-Kategorien mit dem Katalog:
  //   Wärmepumpen · Speicher · Hydraulik · Elektrik · Montage · Beratung.
  hardware:    { label: "Wärmepumpen", icon: PackageIcon,   color: "text-emerald-300" },
  speicher:    { label: "Speicher",    icon: Droplets,      color: "text-sky-300" },
  hydraulisch: { label: "Hydraulik",   icon: Droplets,      color: "text-cyan-300" },
  elektrisch:  { label: "Elektrik",    icon: Zap,           color: "text-amber-300" },
  montage:     { label: "Montage",     icon: Wrench,        color: "text-orange-300" },
  beratung:    { label: "Beratung",    icon: ClipboardList, color: "text-violet-300" },
  frei:        { label: "Frei",        icon: Pencil,        color: "text-zinc-300" },
  addon:       { label: "Sonstiges",   icon: Layers,        color: "text-zinc-300" },
};
// Iter89i — Reihenfolge im Picker identisch zur Backend-Sortierung
// (wp_offer.py::_WP_CAT_ORDER + pdf_generator.py::_CATEGORY_ORDER).
const CAT_ORDER = ["hardware", "speicher", "hydraulisch", "elektrisch", "montage", "beratung"];

const CatIcon = ({ cat, size = 14 }) => {
  const C = (CAT_META[cat] || CAT_META.addon).icon;
  return <C size={size} className={(CAT_META[cat] || CAT_META.addon).color} />;
};

/* -------------------------------------------------------------------------- */

function AddPositionPicker({ open, componentsByCategory, onCancel, onPick, onPickFree }) {
  const [category, setCategory] = useState("hardware");
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const raw = componentsByCategory[category] || [];
    const query = q.trim().toLowerCase();
    if (!query) return raw;
    return raw.filter((c) => `${c.brand} ${c.model}`.toLowerCase().includes(query));
  }, [componentsByCategory, category, q]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3" data-testid="wp-v3-add-picker">
      <div className="bg-[#18181B] border border-zinc-700 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="border-b border-zinc-800 px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[12px] uppercase tracking-widest text-zinc-300">Neue Position</div>
            <div className="font-bold text-[15px] text-zinc-100">Aus Katalog wählen oder freie Zeile</div>
          </div>
          <button onClick={onCancel} className="h-8 px-3 text-[12px] bg-zinc-800 rounded font-bold" data-testid="wp-v3-add-cancel">Abbrechen</button>
        </div>

        <div className="p-3 border-b border-zinc-800 space-y-2">
          <div className="flex gap-1 flex-wrap">
            {CAT_ORDER.map((k) => (
              <button key={k} onClick={() => setCategory(k)}
                data-testid={`wp-v3-add-cat-${k}`}
                className={`h-8 px-3 text-[12px] uppercase tracking-widest rounded-lg font-bold ${category === k ? "bg-[#00E575] text-[#09090B]" : "bg-[#0F0F11] border border-zinc-700 text-zinc-300"}`}>
                {CAT_META[k].label}
              </button>
            ))}
            <button onClick={onPickFree}
              data-testid="wp-v3-add-free"
              className="h-8 px-3 text-[12px] uppercase tracking-widest rounded-lg font-bold bg-[#0F0F11] border border-dashed border-zinc-500 text-zinc-300 hover:border-[#00E575] hover:text-[#00E575]">
              + Freie Zeile
            </button>
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="wp-v3-add-search"
            placeholder="Suchen (Marke, Modell)…"
            className="h-9 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-sm text-zinc-100 focus:border-[#00E575] outline-none" />
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {list.length === 0 && (
            <div className="text-center text-[12px] text-zinc-300 py-6 font-medium">Keine Komponenten. Ggf. andere Kategorie oder freie Zeile.</div>
          )}
          {list.map((c) => (
            <button key={c.id} onClick={() => onPick(c)}
              data-testid={`wp-v3-add-pick-${c.id}`}
              className="w-full text-left rounded-lg border border-zinc-800 hover:border-[#00E575]/50 bg-[#0a0a0c] p-3 flex items-center gap-3">
              <CatIcon cat={c.category} size={16} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[13px] text-zinc-100 truncate">{c.brand} · {c.model}</div>
                <div className="text-[10.5px] text-zinc-500">{c.kw ? `${c.kw} kW · ` : ""}{c.unit || "Stk"}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[12px] font-semibold text-emerald-300">{fmt(c.price_net_eur)}</div>
                <div className="text-[12px] text-zinc-300">netto/Stk</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * @param {object} props
 * @param {number} [props.heatLoadKw]  Heizlast für Empfehlung
 * @param {string} [props.initialPackageId]  Vor-Auswahl (Edit-Mode)
 * @param {Array}  [props.initialLines]      Vor-Auswahl der Zeilen (Edit-Mode)
 * @param {number} [props.initialDiscountPct]
 * @param {function(state):void} [props.onChange]  Meldet {packageId, lines, totals, discountPct}
 */
export default function WpPackageConfiguratorV3({
  heatLoadKw,
  customerId,
  initialPackageId,
  initialLines,
  initialDiscountPct,
  initialDiscountEur,
  editingSnapshot,
  onChange,
}) {
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [componentsByCategory, setComponentsByCategory] = useState({});
  const [selectedPkgId, setSelectedPkgId] = useState(null);
  const [lines, setLines] = useState(() => sortOfferItems(initialLines || []));
  // Iteration 83: Rabatt kann jetzt als Prozentsatz ODER als Euro-Betrag mit
  // 2 Nachkommastellen eingegeben werden. `discountMode` steuert, welches
  // Feld aktiv ist — der jeweils andere Wert wird nicht zurückgesetzt, damit
  // der User beim Toggle nichts verliert.
  const [discountMode, setDiscountMode] = useState(
    (initialDiscountEur && Number(initialDiscountEur) > 0) ? "eur" : "percent",
  );
  const [discountPct, setDiscountPct] = useState(initialDiscountPct || 0);
  const [discountEur, setDiscountEur] = useState(initialDiscountEur || 0);
  const [expandBusy, setExpandBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [packageMeta, setPackageMeta] = useState(null); // {pricing_mode, target_gross}
  // Iter89z-hint (2026-02-26) — Zielpreis-Hinweis dismissable + nur bei
  // Unterschreiten anzeigen. Beim Paket-Wechsel wird der Zustand zurückgesetzt
  // (useEffect auf packageMeta.target_gross).
  const [targetHintDismissed, setTargetHintDismissed] = useState(false);
  const sheetInfo = useWpDatasheets(selectedPkgId, lines);
  useEffect(() => {
    setTargetHintDismissed(false);
  }, [packageMeta?.target_gross]);

  // Load catalog once
  useEffect(() => {
    setLoading(true);
    const params = { ...(heatLoadKw != null ? { heat_load_kw: heatLoadKw } : {}), ...(customerId ? { customer_id: customerId } : {}) };
    api.get("/wp-packages/for-offer", { params })
      .then(({ data }) => {
        setPackages(data.packages || []);
        setComponentsByCategory(data.components_by_category || {});
        if (initialPackageId) {
          setSelectedPkgId(initialPackageId);
          // wenn keine initialen lines → expand
          if (!initialLines || initialLines.length === 0) {
            void loadPackageLines(initialPackageId);
          }
        } else {
          const rec = (data.packages || []).find((p) => p.recommended) || (data.packages || [])[0];
          if (rec) {
            setSelectedPkgId(rec.id);
            void loadPackageLines(rec.id);
          }
        }
      })
      .catch((e) => toast.error(e.response?.data?.detail || "WP-Katalog konnte nicht geladen werden"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heatLoadKw, customerId]);

  const loadPackageLines = async (pkgId, modeOverride = null) => {
    setExpandBusy(true);
    try {
      const params = { ...(modeOverride ? { mode_override: modeOverride } : {}), ...(customerId ? { customer_id: customerId } : {}) };
      const { data } = await api.get(`/wp-packages/${pkgId}/expand-lines`, { params });
      setLines(sortOfferItems(data.lines || []));
      setPackageMeta({
        pricing_mode: data.pricing_mode || "bundle",
        target_gross: data.totals?.target_gross || 0,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Paket konnte nicht geladen werden");
    } finally {
      setExpandBusy(false);
    }
  };

  // Iter88zu (2026-02-18) — Bundle-Zeile in ihre Einzelkomponenten auf-
  // splitten, damit der Vertriebler jede Position (Menge/Preis/Titel)
  // ändern kann. Warnt den User, dass die aktuelle Bundle-Zeile inkl.
  // manueller Änderungen ersetzt wird.
  const explodeBundle = useCallback(async () => {
    if (!selectedPkgId) return;
    const bundleLine = lines.find((L) => L.kind === "bundle");
    if (!bundleLine) return;
    const hasCustomAdditions = lines.some((L) => L.kind !== "bundle" && L.kind !== "component");
    const msg = hasCustomAdditions
      ? "Paket in Einzelpositionen aufsplitten?\n\nDie Paket-Zeile wird durch die enthaltenen Komponenten ersetzt. Zusätzliche freie Zeilen bleiben erhalten."
      : "Paket in Einzelpositionen aufsplitten?\n\nDie Paket-Zeile wird durch die einzelnen Komponenten ersetzt, damit du jede Position einzeln bearbeiten kannst.";
    if (!window.confirm(msg)) return;
    const freeExtras = lines.filter((L) => L.kind === "free");
    setExpandBusy(true);
    try {
      const { data } = await api.get(`/wp-packages/${selectedPkgId}/expand-lines`, {
        params: { mode_override: "line_items", ...(customerId ? { customer_id: customerId } : {}) },
      });
      const explodedLines = data.lines || [];
      // Freie Extras hinten anhängen
      setLines([...sortOfferItems(explodedLines), ...freeExtras]);
      setPackageMeta({
        pricing_mode: "line_items",
        target_gross: data.totals?.target_gross || 0,
      });
      toast.success(`Paket aufgesplittet — ${explodedLines.length} Einzelposition${explodedLines.length !== 1 ? "en" : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Paket konnte nicht aufgesplittet werden");
    } finally {
      setExpandBusy(false);
    }
  }, [selectedPkgId, lines, customerId]);

  const changePkg = async (id) => {
    if (id === selectedPkgId) return;
    if (lines.length > 0) {
      if (!window.confirm("Paket wechseln?\n\nDie aktuellen Zeilen werden ersetzt.")) return;
    }
    setSelectedPkgId(id);
    await loadPackageLines(id);
  };

  const updateLine = useCallback((id, field, value) => {
    setLines((prev) => prev.map((L) => L.id === id ? { ...L, [field]: value } : L));
  }, []);
  const deleteLine = useCallback((id) => {
    setLines((prev) => prev.filter((L) => L.id !== id));
  }, []);
  const addComponentLine = useCallback((c) => {
    const label = `${c.brand || ""} ${c.model || ""}`.trim();
    if (lines.some((L) => L.source_component_id === c.id)
      && !window.confirm(`„${label}“ ist bereits im Angebot.\n\nTrotzdem ein zweites Mal hinzufügen?`)) return;
    if (!(Number(c.price_net_eur) > 0)) toast.warning(`Für „${label}“ ist kein Katalogpreis hinterlegt – bitte Preis in der Zeile eintragen.`);
    const newLine = {
      id: uid(),
      kind: "component",
      category: c.category,
      source_component_id: c.id,
      sku: c.model || "",
      title: `${c.brand} ${c.model}`.trim(),
      description: "",
      quantity: 1,
      unit_price_net: Number(c.price_net_eur) || 0,
      vat_rate: VAT,
    };
    setLines((prev) => sortOfferItems([...prev, newLine]));
    setAddOpen(false);
  }, [lines]);
  const addFreeLine = useCallback(() => {
    setLines((prev) => [...prev, {
      id: uid(), kind: "free", category: "frei",
      sku: "", title: "", description: "", quantity: 1,
      unit_price_net: 0, vat_rate: VAT,
    }]);
    setAddOpen(false);
  }, []);

  // Totals live — HOHE Präzision auf Netto-Summe, erst am Ende runden.
  // (Vermeidet den kaufmännischen Kaskaden-Rundungsfehler, bei dem
  // Σ(round(net×qty)) × 1,19 leicht von round(Σ(net×qty) × 1,19) abweicht.)
  const totals = useMemo(() => {
    const netRaw = lines.reduce((s, L) => s + (Number(L.quantity) || 0) * (Number(L.unit_price_net) || 0), 0);
    // Iteration 83: entweder %-Rabatt ODER €-Rabatt anwenden — aktive Quelle
    // steuert `discountMode`. Der €-Betrag wird auf die Netto-Zwischensumme
    // gedeckelt (nie negativ). Beide Eingaben unabhängig persistiert.
    const discountRaw = discountMode === "eur"
      ? Math.min(Math.max(0, Number(discountEur) || 0), netRaw)
      : netRaw * (Math.max(0, Math.min(100, Number(discountPct) || 0)) / 100);
    const netAfterRaw = Math.max(0, netRaw - discountRaw);
    const vatRaw = netAfterRaw * VAT;
    const grossRaw = netAfterRaw + vatRaw;
    const r = (x) => Math.round(x * 100) / 100;
    return {
      subtotal_net: r(netRaw),
      discount_net: r(discountRaw),
      net: r(netAfterRaw),
      vat: r(vatRaw),
      gross: r(grossRaw),
    };
  }, [lines, discountPct, discountEur, discountMode]);

  // Notify parent
  useEffect(() => {
    if (onChange) onChange({
      packageId: selectedPkgId, lines, totals,
      discountPct: discountMode === "percent" ? discountPct : 0,
      discountEur: discountMode === "eur" ? discountEur : 0,
    });
  }, [selectedPkgId, lines, totals, discountPct, discountEur, discountMode, onChange]);

  if (loading) {
    return (
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-400 text-sm">
        <Loader2 className="animate-spin inline mr-2" /> Katalog wird geladen…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="wp-cfg-v3">
      {editingSnapshot?.id && !editingSnapshot.sent_at && (
        <RepriceBanner offerId={editingSnapshot.id} onApply={(newLines) => setLines(sortOfferItems(newLines))} />
      )}
      {/* Iter89l (2026-02-20) — Live-Angebots-Diff bei Bearbeitung eines
          bereits versendeten Angebots. Zeigt vor dem Speichern was sich
          gegenüber dem gesendeten Angebot geändert hat: Positionen +/-,
          Mengen-Änderungen, Preisdelta, Rabatt-Änderung. */}
      {editingSnapshot && (() => {
        const origItems = Array.isArray(editingSnapshot.items) ? editingSnapshot.items : [];
        // Signatur eines Items zur Wiedererkennung: component_id oder normalisierter Titel.
        const sig = (it) => it.component_id || (it.title || "").trim().toLowerCase();
        const origMap = new Map(origItems.map((it) => [sig(it), it]));
        const currentSigs = new Set(lines.map(sig));
        const added = lines.filter((l) => !origMap.has(sig(l)));
        const removed = origItems.filter((it) => !currentSigs.has(sig(it)));
        const changed = [];
        lines.forEach((l) => {
          const o = origMap.get(sig(l));
          if (!o) return;
          const qtyOld = Number(o.quantity ?? o.qty ?? 0);
          const qtyNew = Number(l.qty ?? l.quantity ?? 0);
          const priceOld = Number(o.unit_price_net ?? o.unit_price_net_eur ?? 0);
          const priceNew = Number(l.unit_price_net_eur ?? l.unit_price_net ?? 0);
          if (qtyOld !== qtyNew || Math.abs(priceOld - priceNew) > 0.005) {
            changed.push({ title: l.title || o.title || "Position", qtyOld, qtyNew, priceOld, priceNew });
          }
        });
        const origDiscPct = Number(editingSnapshot.discount_percent_requested ?? editingSnapshot.discount_percent ?? 0);
        const origDiscEur = Number(editingSnapshot.discount_eur_requested ?? 0);
        const discPctChanged = Math.abs(origDiscPct - Number(discountPct || 0)) > 0.001;
        const discEurChanged = Math.abs(origDiscEur - Number(discountEur || 0)) > 0.005;
        const origTotalNet = Number(editingSnapshot.total_net ?? editingSnapshot.total_net_eur ?? 0);
        const netDelta = Number((totals?.net || 0).toFixed(2)) - Number(origTotalNet.toFixed(2));
        const hasAny = added.length + removed.length + changed.length + (discPctChanged || discEurChanged ? 1 : 0) > 0;
        if (!hasAny) {
          return (
            <div className="rounded-xl border border-zinc-800 bg-[#0F0F11] px-4 py-2.5 text-[12px] text-zinc-400" data-testid="wp-diff-none">
              Keine Änderungen gegenüber Angebot {editingSnapshot.offer_number || "—"} · Netto {fmt(origTotalNet)}
            </div>
          );
        }
        return (
          <div className={`rounded-2xl border p-4 ${editingSnapshot.sent_at ? "border-amber-500/50 bg-amber-500/8" : "border-sky-500/40 bg-sky-500/8"}`} data-testid="wp-diff-panel">
            <div className={`text-[11.5px] font-extrabold uppercase tracking-widest mb-2 inline-flex items-center gap-1.5 ${editingSnapshot.sent_at ? "text-amber-300" : "text-sky-300"}`}>
              <ClipboardList size={13}/> Änderungen ggü. {editingSnapshot.offer_number || "vorheriger Version"}
              <span className={`ml-auto font-mono font-bold ${netDelta >= 0 ? "text-[#00E575]" : "text-red-300"}`}>
                {netDelta >= 0 ? "+" : ""}{fmt(netDelta)} netto
              </span>
            </div>
            <div className="space-y-1 text-[12.5px]">
              {added.map((l, i) => (
                <div key={"a"+i} className="flex items-start gap-2 text-emerald-300" data-testid="wp-diff-added">
                  <Plus size={12} className="mt-0.5 flex-shrink-0"/>
                  <span className="flex-1 min-w-0 truncate"><b>Neu:</b> {l.title || "Position"} · {Number(l.qty || 0)}× {fmt(Number(l.unit_price_net_eur || 0))}</span>
                </div>
              ))}
              {removed.map((o, i) => (
                <div key={"r"+i} className="flex items-start gap-2 text-red-300 line-through" data-testid="wp-diff-removed">
                  <Trash2 size={12} className="mt-0.5 flex-shrink-0"/>
                  <span className="flex-1 min-w-0 truncate"><b>Entfernt:</b> {o.title || "Position"} · {Number(o.quantity || 0)}× {fmt(Number(o.unit_price_net || 0))}</span>
                </div>
              ))}
              {changed.map((c, i) => (
                <div key={"c"+i} className="flex items-start gap-2 text-amber-200" data-testid="wp-diff-changed">
                  <Pencil size={12} className="mt-0.5 flex-shrink-0"/>
                  <span className="flex-1 min-w-0 truncate">
                    <b>Geändert:</b> {c.title}
                    {c.qtyOld !== c.qtyNew && <> · Menge <span className="line-through text-zinc-500">{c.qtyOld}</span> → <b>{c.qtyNew}</b></>}
                    {Math.abs(c.priceOld - c.priceNew) > 0.005 && <> · Preis <span className="line-through text-zinc-500">{fmt(c.priceOld)}</span> → <b>{fmt(c.priceNew)}</b></>}
                  </span>
                </div>
              ))}
              {(discPctChanged || discEurChanged) && (
                <div className="flex items-start gap-2 text-amber-200" data-testid="wp-diff-discount">
                  <Percent size={12} className="mt-0.5 flex-shrink-0"/>
                  <span className="flex-1 min-w-0">
                    <b>Rabatt:</b>&nbsp;
                    {discPctChanged && <><span className="line-through text-zinc-500">{origDiscPct.toFixed(2).replace(".", ",")} %</span> → <b>{Number(discountPct||0).toFixed(2).replace(".", ",")} %</b></>}
                    {discEurChanged && <> {discPctChanged ? " · " : ""}<span className="line-through text-zinc-500">{fmt(origDiscEur)}</span> → <b>{fmt(Number(discountEur||0))}</b></>}
                  </span>
                </div>
              )}
            </div>
            {editingSnapshot.sent_at && (
              <div className="mt-3 pt-3 border-t border-amber-500/30 text-[11.5px] text-amber-100/85">
                Diese Änderungen erscheinen im neuen Angebot, sobald du „Als neues Angebot" wählst.
              </div>
            )}
          </div>
        );
      })()}

      {/* Paket-Auswahl */}
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="text-[12px] uppercase tracking-widest text-zinc-400 font-bold">Paket wählen (als Ausgangsbasis)</div>
          <div
            data-testid="wp-v3-kfw-hint"
            title={`Standard-KfW-458-Annahme ab 09.07.2026: ${KFW_STD_PCT} % Förderung (30 % Grundförderung + 16 % Klimageschwindigkeitsbonus). Bemessungsgrundlage max. ${fmt(KFW_CAP_EUR)}. Die individuelle Förderquote (inkl. Einkommensbonus) wird in Schritt „Förderung" verbindlich berechnet.`}
            className="text-[9.5px] uppercase tracking-widest text-[#00E575]/80 font-extrabold flex items-center gap-1 whitespace-nowrap"
          >
            <span>KfW-Vergleich</span>
            <span className="text-[#00E575]">· {KFW_STD_PCT} %</span>
          </div>
        </div>
        {packages.length === 0 ? (
          <div className="text-[12px] text-zinc-400 font-medium">
            Keine Pakete verfügbar. <a href="/admin/wp-packages" className="text-[#00E575] underline">Katalog anlegen</a> oder
            {" "}<a href="/admin/system-health" className="text-[#00E575] underline">Migration ausführen</a>.
          </div>
        ) : (
          (() => {
            // Nach Hersteller gruppieren (HEIKO, LG, …) — Reihenfolge nach
            // erstem Vorkommen, damit das erwartete Layout stabil bleibt.
            const groups = [];
            const byBrand = {};
            packages.forEach((p) => {
              const b = p.brand || "OHNE MARKE";
              if (!byBrand[b]) {
                byBrand[b] = [];
                groups.push(b);
              }
              byBrand[b].push(p);
            });
            return (
              <div className="space-y-4">
                {groups.map((brand) => (
                  <div key={brand} data-testid={`wp-v3-brand-group-${brand}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="h-[1px] flex-1 bg-gradient-to-r from-[#00E575]/60 to-transparent" />
                      <span className="text-[10.5px] uppercase tracking-[0.2em] font-extrabold text-[#00E575]">
                        {brand}
                      </span>
                      <div className="h-[1px] flex-1 bg-gradient-to-l from-[#00E575]/60 to-transparent" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      {byBrand[brand].map((p) => {
                        const isSel = p.id === selectedPkgId;
                        const k = kfwFor(p.base_price_gross_eur);
                        return (
                          <button key={p.id} onClick={() => changePkg(p.id)}
                            data-testid={`wp-v3-pkg-${p.id}`}
                            className={`text-left rounded-xl p-2.5 border transition ${isSel ? "border-[#00E575] bg-[#0d2a1a]" : "border-zinc-800 bg-[#0a0a0c] hover:border-zinc-600"}`}>
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="font-bold text-[13px] text-zinc-100">{p.kw} kW</span>
                              {p.recommended && <span className="text-[9px] uppercase tracking-widest bg-[#00E575]/20 text-[#00E575] rounded px-1 py-0.5">Empf.</span>}
                            </div>
                            <div className="text-[10.5px] text-zinc-500 line-clamp-1 mb-1.5">{p.name}</div>
                            {/* Brutto — Original-Preis */}
                            <div className="flex items-baseline justify-between gap-1">
                              <span className="text-[9.5px] uppercase tracking-widest text-zinc-500 font-bold">Brutto</span>
                              <span className="font-mono text-[12px] text-zinc-300 font-semibold">{fmt(p.base_price_gross_eur)}</span>
                            </div>
                            {/* Kundenanteil nach KfW (Standard 46 %) */}
                            <div
                              className="mt-1 pt-1.5 border-t border-zinc-800/70 flex items-baseline justify-between gap-1"
                              title={`Standard-KfW-Annahme ab 09.07.2026: ${KFW_STD_PCT} % (30 % Grundförderung + 16 % Klimageschwindigkeitsbonus). Bemessungsgrenze ${fmt(KFW_CAP_EUR)}.`}
                              data-testid={`wp-v3-pkg-${p.id}-kunde-netto`}
                            >
                              <span className="text-[9.5px] uppercase tracking-widest text-[#00E575]/70 font-bold leading-tight">
                                Kunde<br/>zahlt
                              </span>
                              <span className="font-mono text-[13px] text-emerald-300 font-semibold leading-tight">
                                {fmt(k.own)}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()
        )}
        {(sheetInfo.datasheets.length > 0 || sheetInfo.missing.length > 0) && (
          <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between gap-2 flex-wrap" data-testid="wp-v3-datasheet-bar">
            <div className="text-[11.5px] text-zinc-400">
              Datenblatt zur gewählten Wärmepumpe – zum Zeigen im Kundengespräch
            </div>
            <DatasheetButton sheets={sheetInfo.datasheets} missing={sheetInfo.missing} testid="wp-v3-datasheet-btn" />
          </div>
        )}
      </div>

      {/* Zeilen-Tabelle */}
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-3 sm:p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div>
            <div className="text-[12px] uppercase tracking-widest text-zinc-400 font-bold">Angebotspositionen</div>
            <div className="text-[10.5px] text-zinc-500">
              {lines.length} Zeile{lines.length !== 1 ? "n" : ""} · Netto {fmt(totals.subtotal_net)} · <b className="text-[#00E575]">{fmt(totals.gross)}</b> brutto
              {packageMeta?.pricing_mode === "line_items" && (
                <span className="ml-2 text-sky-300">· Modus: Einzelpositionen</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Iter88zu — Bundle-Zeile aufsplitten (nur sichtbar wenn es eine gibt) */}
            {lines.some((L) => L.kind === "bundle") && (
              <button
                onClick={explodeBundle}
                disabled={expandBusy}
                data-testid="wp-v3-explode-bundle"
                title="Paket in einzelne Komponenten aufsplitten, damit du jede Position einzeln bearbeiten kannst"
                className="h-9 px-3 rounded-lg bg-sky-500/15 border border-sky-500/40 text-sky-200 font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-sky-500/25 disabled:opacity-50"
              >
                <Layers size={13} /> Paket aufsplitten
              </button>
            )}
            <button onClick={() => setAddOpen(true)}
              data-testid="wp-v3-open-add"
              className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964]">
              <Plus size={14} /> Position
            </button>
          </div>
        </div>

        {/* Iter89z-hint (2026-02-26) — Nur noch Warnung, wenn die aktuelle
            Summe UNTER dem Paket-Zielpreis liegt (schützt vor „versehentlich
            zu billig verkauft"). Oberhalb des Zielpreises ist Standard-Praxis
            (Zusatzpositionen) und keine Warnung mehr nötig. Außerdem per
            X-Button dismissable – pro Paket-Session. */}
        {packageMeta?.pricing_mode === "line_items" && packageMeta.target_gross > 0
         && totals.gross < packageMeta.target_gross - 1
         && !targetHintDismissed && (
          <div className="mb-3 rounded-lg bg-amber-500/10 border border-amber-500/40 px-3 py-2 pr-9 text-[11.5px] text-amber-200 relative"
               data-testid="wp-v3-target-mismatch">
            <b>Hinweis:</b> Aktuelle Summe {fmt(totals.gross)} liegt <b>unter</b> dem Paket-Zielpreis <b>{fmt(packageMeta.target_gross)}</b>
            (Δ {fmt(totals.gross - packageMeta.target_gross)}).
            <button
              type="button"
              onClick={() => setTargetHintDismissed(true)}
              data-testid="wp-v3-target-mismatch-dismiss"
              className="absolute top-1.5 right-1.5 h-6 w-6 rounded-md hover:bg-amber-500/15 inline-flex items-center justify-center text-amber-300/80 hover:text-amber-200 transition"
              title="Hinweis ausblenden"
            >
              <X size={13} />
            </button>
          </div>
        )}

        {expandBusy && (
          <div className="text-center py-6 text-zinc-300 text-[12px] font-medium"><Loader2 className="animate-spin inline mr-2" size={13} /> Paket wird geladen…</div>
        )}

        {/* Desktop-Tabelle (>=sm) */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-[12px] font-medium" data-testid="wp-v3-table">
            <thead>
              <tr className="text-left text-[12px] uppercase tracking-widest text-zinc-300 border-b border-zinc-800">
                <th className="py-2 pr-2 w-8">Nr.</th>
                <th className="py-2 pr-2 w-24">Art.-Nr.</th>
                <th className="py-2 pr-2">Bezeichnung</th>
                <th className="py-2 pr-2 text-right w-16">Menge</th>
                <th className="py-2 pr-2 text-right w-28">Netto €</th>
                <th className="py-2 pr-2 text-right w-28">Brutto €</th>
                <th className="py-2 pr-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((L, idx) => (
                <LineRow key={L.id} idx={idx} line={L} onChange={updateLine} onDelete={deleteLine} />
              ))}
              {lines.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-zinc-300 text-[12px] font-medium">
                  Keine Positionen. Paket wählen oder Position hinzufügen.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile-Kachel-Ansicht (<sm) */}
        <div className="sm:hidden space-y-2">
          {lines.map((L, idx) => (
            <LineCardMobile key={L.id} idx={idx} line={L} onChange={updateLine} onDelete={deleteLine} />
          ))}
          {lines.length === 0 && (
            <div className="text-center py-6 text-zinc-300 text-[12px] font-medium">Keine Positionen. Paket wählen oder Position hinzufügen.</div>
          )}
        </div>
      </div>

      {/* Rabatt + Summen — Iter89a (2026-02-20): kompletter Redesign.
          Zwei-Zeilen-Card mit klarer Trennung Mode-Switcher / Betrags-Input,
          Live-Vorschau der Ersparnis und farblich abgesetzten Summen. */}
      <div className="bg-gradient-to-br from-[#18181B] to-[#0F0F11] border border-zinc-800 rounded-2xl overflow-hidden shadow-lg">
        {/* Rabatt-Zeile */}
        <div className="p-4 sm:p-5 border-b border-zinc-800/80">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-lg bg-amber-500/12 border border-amber-500/30 flex items-center justify-center">
              <Tag size={15} className="text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-bold text-zinc-100 uppercase tracking-wider">Gesamtrabatt</div>
              <div className="text-[11px] text-zinc-500 leading-tight">Optional — auf die Netto-Zwischensumme</div>
            </div>
            {totals.discount_net > 0 && (
              <div className="hidden sm:flex items-center gap-1.5 text-[11px] font-bold text-amber-200 bg-amber-500/12 border border-amber-500/30 rounded-full px-2.5 py-1 uppercase tracking-widest">
                <Sparkles size={12} /> {fmt(totals.discount_net)} gespart
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
            {/* Segmentierter Umschalter */}
            <div className="inline-flex bg-[#09090B] border border-zinc-700 rounded-xl p-1 self-start" role="tablist" aria-label="Rabatt-Modus">
              <button
                type="button"
                onClick={() => setDiscountMode("percent")}
                data-testid="wp-v3-discount-mode-percent"
                className={`h-9 px-3.5 rounded-lg text-[12.5px] font-bold transition inline-flex items-center gap-1.5 ${discountMode === "percent" ? "bg-[#00E575] text-[#09090B] shadow-md" : "text-zinc-300 hover:text-zinc-100"}`}
              >
                <Percent size={13} strokeWidth={2.5} /> Prozent
              </button>
              <button
                type="button"
                onClick={() => setDiscountMode("eur")}
                data-testid="wp-v3-discount-mode-eur"
                className={`h-9 px-3.5 rounded-lg text-[12.5px] font-bold transition inline-flex items-center gap-1.5 ${discountMode === "eur" ? "bg-[#00E575] text-[#09090B] shadow-md" : "text-zinc-300 hover:text-zinc-100"}`}
              >
                <Euro size={13} strokeWidth={2.5} /> Euro
              </button>
            </div>

            {/* Betrags-Input */}
            <div className="flex-1 flex items-center gap-2">
              {discountMode === "percent" ? (
                <div className="relative flex-1 sm:flex-none">
                  <input type="number" min="0" max="100" step="0.01" value={discountPct}
                    onChange={(e) => setDiscountPct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                    data-testid="wp-v3-discount"
                    placeholder="0,00"
                    className="h-11 w-full sm:w-40 bg-[#09090B] border border-zinc-700 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] rounded-xl pl-4 pr-9 text-right text-[15px] font-mono font-semibold text-zinc-50 outline-none transition" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] font-semibold text-zinc-400 pointer-events-none">%</span>
                </div>
              ) : (
                <div className="relative flex-1 sm:flex-none">
                  <input type="number" min="0" step="0.01" value={discountEur}
                    onChange={(e) => setDiscountEur(Math.max(0, Number(e.target.value) || 0))}
                    data-testid="wp-v3-discount-eur"
                    placeholder="0,00"
                    className="h-11 w-full sm:w-44 bg-[#09090B] border border-zinc-700 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] rounded-xl pl-4 pr-9 text-right text-[15px] font-mono font-semibold text-zinc-50 outline-none transition" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] font-semibold text-zinc-400 pointer-events-none">€</span>
                </div>
              )}
              {/* Live-Vorschau der Ersparnis auf Mobile — auf Desktop zeigt der Badge oben */}
              {totals.discount_net > 0 && (
                <div className="sm:hidden text-[11px] font-bold text-amber-200 bg-amber-500/12 border border-amber-500/30 rounded-full px-2.5 py-1 uppercase tracking-widest inline-flex items-center gap-1">
                  <Sparkles size={11} /> {fmt(totals.discount_net)}
                </div>
              )}
            </div>
          </div>

          {/* Quick-Set Chips für häufige Prozentsätze */}
          {discountMode === "percent" && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="text-[10.5px] font-semibold text-zinc-500 uppercase tracking-widest mr-1">Schnell:</span>
              {[0, 2, 5, 10].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setDiscountPct(p)}
                  data-testid={`wp-v3-discount-quick-${p}`}
                  className={`h-7 px-2.5 rounded-lg text-[11.5px] font-bold border transition ${
                    Number(discountPct) === p
                      ? "bg-[#00E575]/12 border-[#00E575] text-[#00E575]"
                      : "bg-transparent border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500"
                  }`}
                >
                  {p} %
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Summen-Zeile — kompaktes Grid statt vieler Text-Rows */}
        <div className="p-4 sm:p-5 space-y-1.5 font-mono text-[12.5px]">
          <Row label="Zwischensumme netto" value={fmt(totals.subtotal_net)} />
          {totals.discount_net > 0 && (
            <Row
              label={discountMode === "eur" ? "Rabatt" : `Rabatt ${Number(discountPct).toFixed(2).replace(".", ",")} %`}
              value={`− ${fmt(totals.discount_net)}`}
              accent="amber"
            />
          )}
          <Row label="Netto" value={fmt(totals.net)} />
          <Row label={`MwSt. ${(VAT * 100).toFixed(0)} %`} value={fmt(totals.vat)} />
          <div className="pt-2 mt-1 border-t border-zinc-800">
            <Row label="Brutto" value={fmt(totals.gross)} big />
          </div>
        </div>
      </div>

      <AddPositionPicker
        open={addOpen}
        componentsByCategory={componentsByCategory}
        onCancel={() => setAddOpen(false)}
        onPick={addComponentLine}
        onPickFree={addFreeLine}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Row({ label, value, accent, big }) {
  const cls = big
    ? "text-[15px] font-semibold text-emerald-300"
    : accent === "amber" ? "text-amber-300" : "text-zinc-300";
  return (
    <div className={`flex justify-between ${cls}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function LineRow({ idx, line, onChange, onDelete }) {
  const qty = Number(line.quantity) || 0;
  const unitNet = Number(line.unit_price_net) || 0;
  const rate = Number.isFinite(Number(line.vat_rate)) && Number(line.vat_rate) > 0 ? Number(line.vat_rate) : VAT;
  const net = qty * unitNet;
  const gross = Math.round(net * (1 + rate) * 100) / 100;
  const isBundle = line.kind === "bundle";
  return (
    <tr className={`border-b border-zinc-800/60 ${isBundle ? "bg-[#0d2a1a]/40" : ""}`} data-testid={`wp-v3-row-${line.id}`}>
      <td className="py-2 pr-2 text-zinc-500 font-mono">{idx + 1}</td>
      <td className="py-2 pr-2">
        <input value={line.sku || ""} onChange={(e) => onChange(line.id, "sku", e.target.value)}
          placeholder="—"
          className="h-8 w-full bg-transparent border border-transparent hover:border-zinc-700 focus:border-[#00E575] focus:bg-[#09090B] rounded px-2 text-[12px] text-zinc-400 focus:text-zinc-100 outline-none" />
      </td>
      <td className="py-2 pr-2">
        <div className="flex items-center gap-1.5">
          <CatIcon cat={line.category} size={13} />
          <input value={line.title || ""} onChange={(e) => onChange(line.id, "title", e.target.value)}
            placeholder="Bezeichnung"
            data-testid={`wp-v3-title-${line.id}`}
            className={`h-8 flex-1 min-w-0 bg-transparent border border-transparent hover:border-zinc-700 focus:border-[#00E575] focus:bg-[#09090B] rounded px-2 text-[12px] outline-none ${isBundle ? "font-bold text-zinc-100" : "text-zinc-200"}`} />
        </div>
        {line.description && (
          <input value={line.description} onChange={(e) => onChange(line.id, "description", e.target.value)}
            className="h-6 w-full bg-transparent border-0 text-[10.5px] text-zinc-500 focus:text-zinc-300 outline-none px-2" />
        )}
      </td>
      <td className="py-2 pr-2 text-right">
        <input type="number" min="0" step="1" value={line.quantity}
          onChange={(e) => onChange(line.id, "quantity", Math.max(0, Math.round(Number(e.target.value) || 0)))}
          data-testid={`wp-v3-qty-${line.id}`}
          className="h-8 w-16 bg-[#09090B] border border-zinc-700 rounded px-2 text-right text-[12px] font-mono" />
      </td>
      <td className="py-2 pr-2 text-right">
        <div className="w-28 inline-block">
          <EuroInput
            value={Number(line.unit_price_net || 0)}
            onChange={(v) => onChange(line.id, "unit_price_net", Math.max(0, v || 0))}
            testid={`wp-v3-net-${line.id}`}
            className="h-8 w-full bg-[#09090B] border border-zinc-700 rounded px-2 text-[12px] font-mono text-[#00E575] outline-none focus:border-[#00E575]"
          />
        </div>
      </td>
      <td className="py-2 pr-2 text-right font-mono text-zinc-300">{fmt(gross)}</td>
      <td className="py-2 pr-2 text-right">
        <button onClick={() => onDelete(line.id)}
          data-testid={`wp-v3-delete-${line.id}`}
          className="h-7 w-7 rounded bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20 inline-flex items-center justify-center">
          <Trash2 size={11} />
        </button>
      </td>
    </tr>
  );
}

function LineCardMobile({ idx, line, onChange, onDelete }) {
  const qty = Number(line.quantity) || 0;
  const unitNet = Number(line.unit_price_net) || 0;
  const rate = Number.isFinite(Number(line.vat_rate)) && Number(line.vat_rate) > 0 ? Number(line.vat_rate) : VAT;
  const gross = Math.round(qty * unitNet * (1 + rate) * 100) / 100;
  const isBundle = line.kind === "bundle";
  return (
    <div className={`rounded-lg border p-2.5 ${isBundle ? "border-[#00E575]/40 bg-[#0d2a1a]/40" : "border-zinc-800 bg-[#09090B]"}`} data-testid={`wp-v3-card-${line.id}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5 text-[12px] text-zinc-300 font-mono">
          <span>Pos {idx + 1}</span>
          <CatIcon cat={line.category} size={12} />
        </div>
        <button onClick={() => onDelete(line.id)}
          className="h-7 w-7 rounded bg-red-500/10 border border-red-500/40 text-red-400 inline-flex items-center justify-center">
          <Trash2 size={11} />
        </button>
      </div>
      <input value={line.title || ""} onChange={(e) => onChange(line.id, "title", e.target.value)}
        placeholder="Bezeichnung"
        className={`h-8 w-full bg-[#09090B] border border-zinc-700 rounded px-2 text-[12px] outline-none mb-1 ${isBundle ? "font-bold text-zinc-100" : "text-zinc-200"}`} />
      <input value={line.sku || ""} onChange={(e) => onChange(line.id, "sku", e.target.value)}
        placeholder="Art.-Nr."
        className="h-7 w-full bg-transparent border border-zinc-800 rounded px-2 text-[12px] text-zinc-400 outline-none mb-1" />
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-300">Menge</div>
          <input type="number" min="0" step="1" value={line.quantity}
            onChange={(e) => onChange(line.id, "quantity", Math.max(0, Math.round(Number(e.target.value) || 0)))}
            data-testid={`wp-v3-qty-mobile-${line.id}`}
            className="h-9 w-full bg-[#0a0a0c] border border-zinc-700 rounded px-2 text-right text-[13px] font-mono" />
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-300">Netto € / Stk</div>
          <EuroInput
            value={Number(line.unit_price_net || 0)}
            onChange={(v) => onChange(line.id, "unit_price_net", Math.max(0, v || 0))}
            testid={`wp-v3-net-mobile-${line.id}`}
            className="h-8 w-full bg-[#0a0a0c] border border-zinc-700 rounded px-2 text-[12px] font-mono text-[#00E575] outline-none focus:border-[#00E575]"
          />
        </div>
      </div>
      <div className="mt-1.5 pt-1.5 border-t border-zinc-800 flex justify-between text-[12px] font-mono">
        <span className="text-zinc-500">Zeile brutto</span>
        <span className="text-zinc-100 font-bold">{fmt(gross)}</span>
      </div>
    </div>
  );
}
